/* ============================================================
   useReviewPrefs — persisted reader-side review filters (EXPERIMENTAL)

   Two booleans that decide whether revision-aware comment layers show in
   the margin: agent notations and resolved (addressed) earlier-review
   comments. Persisted to localStorage like the theme/font prefs, and shared
   (module-level singleton) between the Settings menu, the reader's quick
   filter control, and DocView's rendering. Default: both shown.
   ============================================================ */

import { ref, readonly } from 'vue'

const SHOW_AGENT_KEY = 'md-reader-show-agent'
const SHOW_RESOLVED_KEY = 'md-reader-show-resolved'

const showAgent = ref(true)
const showResolved = ref(true)

function readBool(key: string, def: boolean): boolean {
  try {
    const v = localStorage.getItem(key)
    return v === null ? def : v !== 'false'
  } catch {
    return def
  }
}
function persist(key: string, val: boolean): void {
  try {
    localStorage.setItem(key, String(val))
  } catch {
    /* storage may be unavailable; pref still applies for the session */
  }
}

let initialized = false
function init(): void {
  if (initialized) return
  initialized = true
  showAgent.value = readBool(SHOW_AGENT_KEY, true)
  showResolved.value = readBool(SHOW_RESOLVED_KEY, true)
}

export function useReviewPrefs() {
  init()
  function setShowAgent(v: boolean): void {
    showAgent.value = v
    persist(SHOW_AGENT_KEY, v)
  }
  function setShowResolved(v: boolean): void {
    showResolved.value = v
    persist(SHOW_RESOLVED_KEY, v)
  }
  return {
    showAgent: readonly(showAgent),
    showResolved: readonly(showResolved),
    setShowAgent,
    setShowResolved,
    toggleAgent: () => setShowAgent(!showAgent.value),
    toggleResolved: () => setShowResolved(!showResolved.value),
  }
}
